import React, { useEffect, useMemo, useRef } from 'react';
import {
  createBrowserRouter, createRoutesFromElements, RouterProvider,
  Route, Navigate, useParams,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { usePermissions } from './hooks/usePermissions';
import { resolveLandingPath } from './lib/landing';
import { usePlatformBranding } from './hooks/usePlatformBranding';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import OrgSelector from './components/OrgSelector';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { useOrgLegalGate } from './hooks/useOrgLegalGate';
import LegalAcceptanceGate from './components/legal/LegalAcceptanceGate';
import Login from './components/Login';
import GroupsList from './components/GroupsList';
import Hoy from './components/Hoy';
import GroupDetails from './components/GroupDetails';
import AttendanceScreen from './components/AttendanceScreen';
import TrainingAgendaMonthly from './components/agenda/TrainingAgendaMonthly';
import ActivateAccount from './components/ActivateAccount';
import ResetPassword from './components/ResetPassword';
import AceptarInvitacionTorneo from './components/AceptarInvitacionTorneo';
import ConvocatoriaPublica from './components/ConvocatoriaPublica';
import ConsentimientoFirma from './components/ConsentimientoFirma';
import GroupManagement from './components/admin/GroupManagement';
import CategoriesManager from './components/admin/CategoriesManager';
import OrganizationSettings from './components/admin/OrganizationSettings';
import StaffManagement from './components/StaffManagement';
import PlayersManager from './components/admin/PlayersManager';
import FichasIncompletas from './components/admin/FichasIncompletas';
import PlayerProfilePage from './components/admin/PlayerProfilePage';
import PlayerFormPage from './components/admin/PlayerFormPage';
import ConsentimientosDashboard from './components/admin/ConsentimientosDashboard';
import PagosDashboard from './components/admin/pagos/PagosDashboard';
import PagosRegistro from './components/admin/pagos/PagosRegistro';
import PagosEstadoCuenta from './components/admin/pagos/PagosEstadoCuenta';
import PagosPlanes from './components/admin/pagos/PagosPlanes';
import PagosConceptos from './components/admin/pagos/PagosConceptos';
import PagosPromociones from './components/admin/pagos/PagosPromociones';
import PagosFamilias from './components/admin/pagos/PagosFamilias';
import PrendasPage from './components/admin/uniformes/PrendasPage';
import TallasPage from './components/admin/uniformes/TallasPage';
import RepartoPage from './components/admin/uniformes/RepartoPage';
import ExtrasPage from './components/admin/uniformes/ExtrasPage';
import TemporadaPrendasPage from './components/admin/uniformes/TemporadaPrendasPage';
import PedidosPage from './components/admin/uniformes/PedidosPage';
import PedidoDetallePage from './components/admin/uniformes/PedidoDetallePage';
import InventarioArticulos from './components/admin/inventario/InventarioArticulos';
import InventarioPrestamos from './components/admin/inventario/InventarioPrestamos';
import InventarioPorJugador from './components/admin/inventario/InventarioPorJugador';
import InventarioConteos from './components/admin/inventario/InventarioConteos';
import ConteoDetalle from './components/admin/inventario/ConteoDetalle';
import InventarioReporte from './components/admin/inventario/InventarioReporte';
import PagosAsignaciones from './components/admin/pagos/PagosAsignaciones';
import PagosSolicitudes from './components/admin/pagos/PagosSolicitudes';
import EstadoCuentaPublic from './components/public/EstadoCuentaPublic';
import SeriesEntrenamientoManager from './components/SeriesEntrenamientoManager';
import TemporadasManager from './components/TemporadasManager';
import InscripcionesManager from './components/admin/inscripciones/InscripcionesManager';
import ReporteAsistenciaMensual from './components/ReporteAsistenciaMensual';
import RootLayout from './components/layouts/RootLayout';
import Torneos from './components/torneos/Torneos';
import TorneoDetail from './components/torneos/TorneoDetail';
import InscripcionDetail from './components/torneos/InscripcionDetail';
import GameAttendanceScreen from './components/GameAttendanceScreen';
import MisJuegos from './components/torneos/MisJuegos';
import JuegosDeLaRed from './components/torneos/JuegosDeLaRed';
import JuegosOverview from './components/torneos/JuegosOverview';
import MyklubbTorneos from './components/torneos/MyklubbTorneos';
import MyklubbInvitaciones from './components/torneos/MyklubbInvitaciones';
import ParticipacionDetail from './components/torneos/ParticipacionDetail';
import JuegoDetailView from './components/torneos/JuegoDetailView';
import CredencialesRival from './components/torneos/CredencialesRival';
import LigasManager from './components/catalogos/LigasManager';
import { MUNDO_INTERNO, MUNDO_RED } from './lib/mundoTorneo';
import EquiposManager from './components/catalogos/EquiposManager';
import NotFoundPage from './components/errors/NotFoundPage';
import TrainingProgramsPage from './components/training/TrainingProgramsPage';
import TrainingTodayProgram from './components/training/TrainingTodayProgram';
import ModoEntrenamientoEnVivo from './components/training/ModoEntrenamientoEnVivo';
import HabilidadesPage from './components/training/HabilidadesPage';
import PlantillasSeccionesPage from './components/training/PlantillasSeccionesPage';
import ProgramaImprimibleView from './components/training/ProgramaImprimibleView';
import ProgramaDetalleView from './components/training/ProgramaDetalleView';
import TarjetonPreview from './components/tarjeton/TarjetonPreview';
import PlatformDashboard from './components/platform/PlatformDashboard';
import PlatformOrganizations from './components/platform/PlatformOrganizations';
import PlatformBranding from './components/platform/PlatformBranding';
import PlatformLegal from './components/platform/PlatformLegal';
import PlatformCrossOrg from './components/platform/PlatformCrossOrg';
import PlatformPlanRequests from './components/platform/PlatformPlanRequests';
import DeportesOrgPage from './components/platform/DeportesOrgPage';
import Profile from './components/Profile';
import CentroAyuda from './components/ayuda/CentroAyuda';
import PlanComparison from './components/billing/PlanComparison';
import RequireModule from './components/billing/RequireModule';


// /torneos/:id/rol vivia suelto; ahora es una pestana de la participacion. Se conserva
// redirigiendo porque hay enlaces compartidos por WhatsApp apuntando ahi.
function RedirigirAParticipacion() {
  const { id } = useParams();
  return <Navigate to={`/torneos-myklubb/${id}`} replace />;
}

// Las rutas viven en una funcion para que las pruebas puedan montarlas con
// createMemoryRouter y comprobar que cada guarda sigue mandando a donde mandaba.
// El cuerpo es identico al que vivia dentro de <Routes>: ni una ruta cambio.
//
// INVARIANTE: esto emite SIEMPRE las mismas 98 rutas, en el mismo orden, pasen lo
// que pasen los permisos. Lo que cambia con el permiso es el `element` de cada una,
// nunca si la ruta existe. Los ids que reparte createRoutesFromElements son
// posicionales y RouterProvider conserva el `state` del router viejo cuando el
// router cambia de identidad: si una rama se montara o desmontara segun un permiso,
// los ids se desalinearian y el usuario veria la pantalla de sus permisos
// anteriores hasta navegar a otro lado.
export function construirRutas({
  user, isAdmin, isPlatformAdmin, canManageTorneos, canManagePrograms,
  landingPath, destinoTrasLogin,
}) {
  return (
    <Route element={<RootLayout />}>
          {/* Rutas públicas */}
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to={destinoTrasLogin} />}
          />
          <Route
            path="/activate/:token"
            element={<ActivateAccount />}
          />
          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="/aceptar-invitacion"
            element={<AceptarInvitacionTorneo />}
          />
          <Route
            path="/convocatoria"
            element={<ConvocatoriaPublica />}
          />
          <Route
            path="/consentimiento/:token"
            element={<ConsentimientoFirma />}
          />

          {/* Rutas protegidas */}
          <Route
            path="/agenda"
            element={user ? <TrainingAgendaMonthly /> : <Navigate to="/login" />}
          />
          <Route
            path="/programas"
            element={<Navigate to={user ? '/programas/agenda' : '/login'} />}
          />
          <Route
            path="/programas/hoy"
            element={user ? <RequireModule feature="programs"><TrainingTodayProgram /></RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/programas/agenda"
            element={user ? <RequireModule feature="programs"><TrainingProgramsPage view="agenda" /></RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/programas/biblioteca"
            element={
              user
                ? <RequireModule feature="programs">{canManagePrograms ? <TrainingProgramsPage view="biblioteca" /> : <Navigate to="/programas/agenda" />}</RequireModule>
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/entrenamiento/:sesionId/en-vivo"
            element={user ? <RequireModule feature="programs"><ModoEntrenamientoEnVivo /></RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/programas/habilidades"
            element={
              user
                ? <RequireModule feature="programs">{canManagePrograms ? <HabilidadesPage /> : <Navigate to="/programas/agenda" />}</RequireModule>
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/programas/plantillas-secciones"
            element={
              user
                ? <RequireModule feature="programs">{canManagePrograms ? <PlantillasSeccionesPage /> : <Navigate to="/programas/agenda" />}</RequireModule>
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/programas/:programaId/imprimible"
            element={user ? <RequireModule feature="programs"><ProgramaImprimibleView /></RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/programas/:programaId"
            element={user ? <RequireModule feature="programs"><ProgramaDetalleView /></RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/hoy"
            element={user ? <Hoy /> : <Navigate to="/login" />}
          />
          <Route
            path="/groups"
            element={user ? <GroupsList /> : <Navigate to="/login" />}
          />
          <Route
            path="/groups/:groupId"
            element={user ? <GroupDetails /> : <Navigate to="/login" />}
          />
          {/* La pestana del grupo vive en la URL: recargar deja donde estabas y el
              enlace de una pestana se puede compartir. Sin seccion abre la primera, y
              una seccion inventada tambien. */}
          <Route
            path="/groups/:groupId/:seccion"
            element={user ? <GroupDetails /> : <Navigate to="/login" />}
          />
          <Route
            path="/attendance/:groupId/:eventId"
            element={user ? <AttendanceScreen /> : <Navigate to="/login" />}
          />
          <Route
            path="/attendance/:groupId"
            element={user ? <AttendanceScreen /> : <Navigate to="/login" />}
          />
          <Route
            path="/attendance/juego/:groupId/:juegoId"
            element={user ? <GameAttendanceScreen /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/groups"
            element={
              user
                ? (isAdmin ? <GroupManagement /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/categorias"
            element={
              user
                ? (isAdmin ? <CategoriesManager /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/staff"
            element={
              user
                ? (isAdmin ? <StaffManagement /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/jugadores"
            element={
              user
                ? (isAdmin ? <PlayersManager /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/jugadores/incompletas"
            element={
              user
                ? (isAdmin ? <FichasIncompletas /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/jugadores/nuevo"
            element={
              user
                ? (isAdmin ? <PlayerFormPage /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/jugadores/:id"
            element={
              user
                ? (isAdmin ? <PlayerProfilePage /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/jugadores/:id/editar"
            element={
              user
                ? (isAdmin ? <PlayerFormPage /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/consentimientos"
            element={
              user
                ? (isAdmin ? <ConsentimientosDashboard /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/pagos"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosDashboard /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/registro"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosRegistro /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/estado-cuenta"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosEstadoCuenta /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/planes"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosPlanes /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/conceptos"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosConceptos /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/promociones"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosPromociones /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/familias"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosFamilias /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/asignaciones"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosAsignaciones /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/pagos/solicitudes"
            element={user ? <RequireModule feature="payments_module">{isAdmin ? <PagosSolicitudes /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <PrendasPage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes/tallas"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <TallasPage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes/reparto"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <RepartoPage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes/extras"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <ExtrasPage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes/temporada"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <TemporadaPrendasPage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes/pedidos"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <PedidosPage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/uniformes/pedidos/:id"
            element={user ? <RequireModule feature="uniforms_module">{isAdmin ? <PedidoDetallePage /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/inventario"
            element={user ? <RequireModule feature="inventory_module">{isAdmin ? <InventarioArticulos /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/inventario/prestamos"
            element={user ? <RequireModule feature="inventory_module">{isAdmin ? <InventarioPrestamos /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/inventario/jugadores"
            element={user ? <RequireModule feature="inventory_module">{isAdmin ? <InventarioPorJugador /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/inventario/conteos"
            element={user ? <RequireModule feature="inventory_module">{isAdmin ? <InventarioConteos /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/inventario/conteos/:id"
            element={user ? <RequireModule feature="inventory_module">{isAdmin ? <ConteoDetalle /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/inventario/reporte"
            element={user ? <RequireModule feature="inventory_module">{isAdmin ? <InventarioReporte /> : <Navigate to="/groups" />}</RequireModule> : <Navigate to="/login" />}
          />
          <Route
            path="/torneos"
            element={user ? <Torneos /> : <Navigate to="/login" />}
          />
          <Route
            path="/torneos/juegos"
            element={user ? <MisJuegos /> : <Navigate to="/login" />}
          />

          {/* MYKLUBB Torneos: modulo propio, no una pestana. Es la relacion del club
              con la red -- invitaciones, participaciones, y lo que venga despues
              (descubrir torneos, avisos de cambios, cuotas). */}
          {/* La lista la ve cualquiera del club: el coach necesita saber cuando y
              donde juega, que es para lo que se abre esta pantalla. Inscribir un grupo
              si es de admin, y eso se decide adentro. */}
          <Route
            path="/torneos-myklubb"
            element={user ? <MyklubbTorneos /> : <Navigate to="/login" />}
          />
          {/* La pareja de Juegos en la casa de la red: solo lectura, solo partidos que
              programa una organizadora ajena. */}
          <Route
            path="/torneos-myklubb/juegos"
            element={user ? <JuegosDeLaRed /> : <Navigate to="/login" />}
          />
          {/* El mes completo, los dos mundos etiquetados: consulta, no captura. */}
          <Route
            path="/agenda/juegos"
            element={user ? <JuegosOverview /> : <Navigate to="/login" />}
          />
          <Route
            path="/torneos-myklubb/invitaciones"
            element={
              user
                ? (canManageTorneos ? <MyklubbInvitaciones /> : <Navigate to="/torneos" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/torneos-myklubb/:id"
            element={user ? <ParticipacionDetail /> : <Navigate to="/login" />}
          />
          {/* Los nombres viejos siguen funcionando: hay enlaces en WhatsApp y correos. */}
          <Route
            path="/torneos/externos"
            element={<Navigate to="/torneos-myklubb" replace />}
          />
          <Route
            path="/torneos/externos/:id"
            element={<RedirigirAParticipacion />}
          />
          <Route
            path="/torneos/invitaciones"
            element={<Navigate to="/torneos-myklubb/invitaciones" replace />}
          />
          {/* Agenda de juegos y la lista de juegos eran la misma informacion en dos
              lugares. Ahora es una pantalla con dos vistas. */}
          <Route
            path="/torneos/agenda"
            element={<Navigate to="/torneos/juegos" replace />}
          />
          <Route
            path="/torneos/mis-juegos"
            element={<Navigate to="/torneos/juegos" replace />}
          />
          <Route
            path="/torneos/juegos/gestion"
            element={<Navigate to="/torneos/juegos" replace />}
          />
          <Route
            path="/torneos/juegos/:juegoId"
            element={user ? <JuegoDetailView /> : <Navigate to="/login" />}
          />
          <Route
            path="/torneos/juegos/:juegoId/rival"
            element={user ? <CredencialesRival /> : <Navigate to="/login" />}
          />
          {/* Las estadisticas viven en la pestana del torneo, que es donde tienen
              contexto. Esta ruta se conserva redirigiendo para no romper enlaces
              guardados. */}
          <Route
            path="/torneos/estadisticas"
            element={<Navigate to="/torneos" replace />}
          />
          <Route
            path="/tarjeton/preview"
            element={user ? <TarjetonPreview /> : <Navigate to="/login" />}
          />
          <Route
            path="/torneos/:torneoId/inscripciones/:inscripcionId"
            element={user ? <InscripcionDetail /> : <Navigate to="/login" />}
          />
          {/* El rol suelto se absorbio en la participacion: era una de las 3 rutas
              donde vivian dispersas las piezas del torneo externo. Redirige. */}
          <Route
            path="/torneos/:id/rol"
            element={<RedirigirAParticipacion />}
          />
          <Route
            path="/torneos/:torneoId"
            element={user ? <TorneoDetail /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/torneos"
            element={user ? <Navigate to="/torneos" /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/torneos/:torneoId"
            element={user ? <Navigate to="/torneos" /> : <Navigate to="/login" />}
          />
          {/* Dos casas para las ligas, como para los torneos: las MIAS se crean aqui
              (nombran la competencia de afuera de un torneo interno); las DE LA RED
              solo se ven -- son de la organizadora del torneo de MYKLUBB Torneos. */}
          <Route
            path="/torneos/ligas"
            element={
              user
                ? (canManageTorneos ? <LigasManager mundo={MUNDO_INTERNO} /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/torneos-myklubb/ligas"
            element={
              user
                ? (canManageTorneos ? <LigasManager mundo={MUNDO_RED} /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/torneos/equipos"
            element={
              user
                ? (canManageTorneos ? <EquiposManager /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          {/* Rutas viejas: el menu siempre las mostro bajo Torneos, la URL decia Catalogos */}
          <Route path="/catalogos/ligas" element={<Navigate to="/torneos/ligas" replace />} />
          <Route path="/catalogos/equipos" element={<Navigate to="/torneos/equipos" replace />} />
          <Route
            path="/admin/entrenamientos-regulares"
            element={
              user
                ? (canManagePrograms ? <SeriesEntrenamientoManager /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/temporadas"
            element={
              user
                ? (isAdmin ? <TemporadasManager /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/inscripciones"
            element={
              user
                ? (isAdmin ? <InscripcionesManager /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/organizacion"
            element={
              user
                ? (isAdmin ? <OrganizationSettings /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          {/* Mi perfil */}
          <Route
            path="/perfil"
            element={user ? <Profile /> : <Navigate to="/login" />}
          />
          {/* Centro de ayuda */}
          <Route
            path="/ayuda"
            element={user ? <CentroAyuda /> : <Navigate to="/login" />}
          />
          {/* Billing: comparativa de planes */}
          <Route
            path="/billing/plans"
            element={user ? <PlanComparison /> : <Navigate to="/login" />}
          />
          {/* Platform Admin */}
          <Route
            path="/platform"
            element={
              user
                ? (isPlatformAdmin ? <PlatformDashboard /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/platform/dashboard"
            element={<Navigate to="/platform" replace />}
          />
          <Route
            path="/platform/organizations"
            element={
              user
                ? (isPlatformAdmin ? <PlatformOrganizations /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/platform/organizations/:orgId/deportes"
            element={
              user
                ? (isPlatformAdmin ? <DeportesOrgPage /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/platform/branding"
            element={
              user
                ? (isPlatformAdmin ? <PlatformBranding /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/platform/legal"
            element={
              user
                ? (isPlatformAdmin ? <PlatformLegal /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/platform/cross-org"
            element={
              user
                ? (isPlatformAdmin ? <PlatformCrossOrg /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/platform/plan-requests"
            element={
              user
                ? (isPlatformAdmin ? <PlatformPlanRequests /> : <Navigate to="/groups" />)
                : <Navigate to="/login" />
            }
          />

          <Route
            path="/entrenamientos/asistencia-mensual"
            element={user ? <ReporteAsistenciaMensual /> : <Navigate to="/login" />}
          />

          {/* Estado de cuenta publico (QR + PIN, sin login) */}
          <Route
            path="/cuenta/:token"
            element={<EstadoCuentaPublic />}
          />

          {/* Ruta por defecto */}
          <Route
            path="/"
            element={<Navigate to={user ? landingPath : "/login"} />}
          />
          
          {/* 404 */}
          <Route 
            path="*" 
            element={<NotFoundPage />} 
          />          
    </Route>
  );
}

function AppContent() {
  const { user, loading, isPlatformAdmin } = useAuth();
  const { myOrgs, isOrgOverridden } = useOrganization();
  const { isAdmin, isHeadCoach, canManagePrograms, canManageTorneos } = usePermissions();
  const branding = usePlatformBranding();
  const legalGate = useOrgLegalGate();
  const landingPath = resolveLandingPath({ isPlatformAdmin, isAdmin, isHeadCoach });

  // A donde volver despues de entrar. Lo usa el enlace de invitacion a torneo, que
  // manda a iniciar sesion y necesita regresar a la misma pantalla con su token.
  // Solo rutas internas: un `next` con esquema o con // seria un redirect abierto.
  const destinoTrasLogin = (() => {
    const next = new URLSearchParams(window.location.search).get('next');
    return next && next.startsWith('/') && !next.startsWith('//') ? next : landingPath;
  })();

  // Las dependencias son primitivas a proposito. `user` es un objeto: si cambiara de
  // identidad en cada render, el router se reconstruiria sin parar.
  const userId = user?.id || null;
  const router = useMemo(
    () => createBrowserRouter(createRoutesFromElements(construirRutas({
      user, isAdmin, isPlatformAdmin, canManageTorneos, canManagePrograms,
      landingPath, destinoTrasLogin,
    }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, isAdmin, isPlatformAdmin, canManageTorneos, canManagePrograms,
     landingPath, destinoTrasLogin],
  );

  // createBrowserRouter arranca escuchando el historial y RouterProvider NUNCA lo
  // libera. Cada vez que cambia una dependencia nace un router nuevo y el viejo se
  // queda oyendo popstate para siempre -- pasa en toda carga de pagina, porque el
  // primero se construye durante el splash con la sesion sin resolver. Con un
  // bloqueador de salida montado, dos routers atendiendo el mismo "atras" se pelean.
  // `dispose` no esta en el contrato publico documentado: por eso el `?.`.
  //
  // Se libera el ANTERIOR al montar, nunca el actual al limpiar: en desarrollo
  // StrictMode monta, limpia y vuelve a montar los efectos, y hacerlo en la limpieza
  // mataba el router VIVO (adios boton "atras" en /login y en las rutas publicas por
  // token, donde ninguna dependencia cambia entre el splash y la sesion resuelta).
  // El precio es que el ultimo router no se libera al desmontar la app; eso solo pasa
  // cuando la pagina entera se va, y entonces se lo lleva el navegador.
  const routerAnterior = useRef(null);
  useEffect(() => {
    if (routerAnterior.current && routerAnterior.current !== router) {
      routerAnterior.current.dispose?.();
    }
    routerAnterior.current = router;
  }, [router]);

  // Se espera tambien a la puerta legal. Sin esto, mientras la organizacion se
  // desconoce la app pintaba el contenido y el bloqueo caia encima despues -- y en esa
  // ventana se podia navegar y escribir sin haber aceptado nada.
  if (loading || legalGate.loading) {
    // Splash de carga: mismo fondo que el Login (foto + vignette + glow volt)
    // para que la secuencia splash del sistema -> app -> login se sienta continua.
    return (
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ backgroundColor: branding.bg }}>
        <div className="pointer-events-none absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/assets/login-bg.jpg)', opacity: 0.95 }} />
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(circle at 50% 44%, ${branding.bg}99 0%, ${branding.bg}40 26%, transparent 46%)` }} />
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 50% 3%, ${branding.accent}1A, transparent 50%)` }} />
        <div className="relative text-center">
          {/* Glow volt detras del logo, mismo acento que el barrido del splash */}
          <div
            className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ width: '200px', height: '200px', background: `radial-gradient(circle, ${branding.accent}26, transparent 68%)` }}
          />
          <div className="mk-breathe relative">
            <img
              src={branding.logo}
              alt={branding.name}
              className="h-16 mx-auto object-contain"
              onError={(e) => { e.target.src = '/assets/myklubb-logo-v-light.svg'; }}
            />
          </div>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: `${branding.text}8C` }}>
            Cargando...
          </p>
        </div>
      </div>
    );
  }

  // Puerta legal: la org no se activa hasta que su admin acepte DPA + Contrato.
  // Excepto en rutas de auth/públicas (p. ej. el link de invitación autentica y
  // debe poder llegar a /reset-password a crear su contraseña ANTES de la puerta).
  const gateExempt = ['/reset-password', '/activate', '/consentimiento', '/cuenta', '/aceptar-invitacion']
    .some((p) => window.location.pathname.startsWith(p));
  if (user && legalGate.gated && !gateExempt) {
    return (
      <LegalAcceptanceGate
        pendientes={legalGate.pendientes}
        canAccept={legalGate.isAdmin}
        orgName={legalGate.orgName}
        orgId={legalGate.orgId}
        esActualizacion={legalGate.esActualizacion}
        onAccepted={legalGate.refetch}
      />
    );
  }

  // Aislamiento duro: con 2+ orgs el usuario elige cual gestionar antes de entrar.
  // Single-org entra directo; platform_admin usa impersonacion (no este flujo);
  // rutas publicas (reset/activate/consentimiento/cuenta) quedan exentas.
  if (user && !isPlatformAdmin && myOrgs.length > 1 && !isOrgOverridden && !gateExempt) {
    return <OrgSelector />;
  }

  return <RouterProvider router={router} />;
}

function App() {
  return (
    <AuthProvider>
      <OrganizationProvider>
        <EntitlementsProvider>
          <UnsavedChangesProvider>
            <AppContent />
          </UnsavedChangesProvider>
        </EntitlementsProvider>
      </OrganizationProvider>
    </AuthProvider>
  );
}

export default App;


